import { Download } from "lucide-react"
import { cn } from "@/lib/utils"
import { Button } from "../Button/Button"
import type { Invoice } from "../types/payment"

export interface InvoicesTableProps {
  invoices: Invoice[]
  invoiceHeader?: string
  amountHeader?: string
  cardHeader?: string
  dateHeader?: string
  statusHeader?: string
  descriptionHeader?: string
  paidDateHeader?: string
  typeHeader?: string
  actionsHeader?: string
  showCard?: boolean
  showDescription?: boolean
  showPaidDate?: boolean
  showType?: boolean
  payLabel?: string
  payAriaLabel?: (invoice: Invoice) => string
  onPay?: (invoice: Invoice) => void
  mobileDownloadSrLabel?: string
  downloadAriaLabel?: (invoice: Invoice) => string
  onDownload?: (invoice: Invoice) => void
  className?: string
}

const TONE_CLASS: Record<NonNullable<Invoice["statusTone"]>, string> = {
  success: "text-success",
  danger: "text-danger",
  muted: "text-muted-foreground",
}

export function InvoicesTable({
  invoices,
  invoiceHeader = "Invoice",
  amountHeader = "Amount",
  cardHeader = "Card",
  dateHeader = "Date",
  statusHeader = "Status",
  descriptionHeader = "Description",
  paidDateHeader = "Paid",
  typeHeader = "Type",
  actionsHeader = "",
  showCard = true,
  showDescription = false,
  showPaidDate = false,
  showType = false,
  payLabel = "Pay",
  payAriaLabel,
  onPay,
  mobileDownloadSrLabel = "Download invoice",
  downloadAriaLabel,
  onDownload,
  className,
}: InvoicesTableProps) {
  const showActions = Boolean(onPay || onDownload)

  return (
    <div
      className={cn(
        "bg-card border border-border rounded-md overflow-hidden",
        className,
      )}
    >
      <table className="w-full">
        <thead>
          <tr className="border-b border-border">
            {showActions && (
              <th className="w-10 px-2 py-3 sm:hidden">
                <span className="sr-only">{mobileDownloadSrLabel}</span>
              </th>
            )}
            <th className="text-caption text-muted-foreground text-left px-5 py-3 hidden sm:table-cell">
              {invoiceHeader}
            </th>
            {showType && (
              <th className="text-caption text-muted-foreground text-left px-3 sm:px-5 py-3 whitespace-nowrap hidden sm:table-cell">
                {typeHeader}
              </th>
            )}
            {showDescription && (
              <th className="text-caption text-muted-foreground text-left px-3 sm:px-5 py-3 hidden sm:table-cell">
                {descriptionHeader}
              </th>
            )}
            <th className="text-caption text-muted-foreground text-left px-3 sm:px-5 py-3 whitespace-nowrap">
              {amountHeader}
            </th>
            {showCard && (
              <th className="text-caption text-muted-foreground text-left px-3 sm:px-5 py-3 whitespace-nowrap">
                {cardHeader}
              </th>
            )}
            <th className="text-caption text-muted-foreground text-left px-3 sm:px-5 py-3 whitespace-nowrap">
              {dateHeader}
            </th>
            {showPaidDate && (
              <th className="text-caption text-muted-foreground text-left px-3 sm:px-5 py-3 whitespace-nowrap hidden sm:table-cell">
                {paidDateHeader}
              </th>
            )}
            <th className="text-caption text-muted-foreground text-left px-3 sm:px-5 py-3 whitespace-nowrap">
              {statusHeader}
            </th>
            {showActions && (
              <th className="text-caption text-muted-foreground text-right px-3 sm:px-5 py-3 whitespace-nowrap hidden sm:table-cell">
                {actionsHeader ? actionsHeader : <span className="sr-only">Actions</span>}
              </th>
            )}
          </tr>
        </thead>
        <tbody>
          {invoices.map((inv) => {
            const handleDownload = onDownload ? () => onDownload(inv) : undefined
            const handlePay = onPay && inv.payable ? () => onPay(inv) : undefined
            const downloadAria = downloadAriaLabel
              ? downloadAriaLabel(inv)
              : `Download ${inv.id}`
            const payAria = payAriaLabel ? payAriaLabel(inv) : `Pay ${inv.id}`
            const statusToneClass = inv.statusTone
              ? TONE_CLASS[inv.statusTone]
              : "text-foreground"

            return (
              <tr key={inv.id} className="border-b border-border last:border-b-0">
                {showActions && (
                  <td className="w-10 px-2 py-3 align-middle sm:hidden">
                    {handlePay ? (
                      <Button
                        type="button"
                        size="sm"
                        className="text-caption"
                        onClick={handlePay}
                        aria-label={payAria}
                      >
                        {payLabel}
                      </Button>
                    ) : handleDownload ? (
                      <button
                        type="button"
                        onClick={handleDownload}
                        title={inv.id}
                        aria-label={downloadAria}
                        className="inline-flex items-center justify-center w-7 h-7 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-colors cursor-pointer"
                      >
                        <Download size={14} strokeWidth={1.75} />
                      </button>
                    ) : null}
                  </td>
                )}
                <td className="text-caption px-5 py-3 hidden sm:table-cell">
                  {handleDownload ? (
                    <button
                      type="button"
                      onClick={handleDownload}
                      className="text-primary hover:text-primary/80 transition-colors cursor-pointer"
                    >
                      {inv.id}
                    </button>
                  ) : (
                    <span className="text-foreground">{inv.id}</span>
                  )}
                </td>
                {showType && (
                  <td className="text-caption text-muted-foreground px-3 sm:px-5 py-3 whitespace-nowrap hidden sm:table-cell">
                    {inv.type ?? "—"}
                  </td>
                )}
                {showDescription && (
                  <td className="text-caption text-muted-foreground px-3 sm:px-5 py-3 hidden sm:table-cell">
                    <span className="block max-w-[24ch] truncate" title={inv.description}>
                      {inv.description ?? "—"}
                    </span>
                  </td>
                )}
                <td className="text-caption text-foreground px-3 sm:px-5 py-3 whitespace-nowrap">
                  {inv.amount}
                </td>
                {showCard && (
                  <td className="text-caption text-foreground px-3 sm:px-5 py-3 whitespace-nowrap">
                    {inv.card}
                  </td>
                )}
                <td className="text-caption text-foreground px-3 sm:px-5 py-3 whitespace-nowrap">
                  {inv.date}
                </td>
                {showPaidDate && (
                  <td className="text-caption text-foreground px-3 sm:px-5 py-3 whitespace-nowrap hidden sm:table-cell">
                    {inv.paidDate ?? "—"}
                  </td>
                )}
                <td
                  className={cn(
                    "text-caption px-3 sm:px-5 py-3 whitespace-nowrap",
                    statusToneClass,
                  )}
                >
                  {inv.status}
                </td>
                {showActions && (
                  <td className="px-3 sm:px-5 py-3 whitespace-nowrap text-right hidden sm:table-cell">
                    {handlePay ? (
                      <Button
                        type="button"
                        size="sm"
                        className="text-label"
                        onClick={handlePay}
                        aria-label={payAria}
                      >
                        {payLabel}
                      </Button>
                    ) : handleDownload ? (
                      <button
                        type="button"
                        onClick={handleDownload}
                        title={inv.id}
                        aria-label={downloadAria}
                        className="inline-flex items-center justify-center w-7 h-7 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-colors cursor-pointer"
                      >
                        <Download size={14} strokeWidth={1.75} />
                      </button>
                    ) : null}
                  </td>
                )}
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}
